import { Box, Grow, IconButton, Stack, Typography, useTheme } from '@mui/material';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { CancelTicketChip } from '@/features/wallet/components/ticket-cancel/cancel-ticket-chip';
import { DateTime } from '../../utility/luxon';
import { FC } from 'react';
import { ILocale } from '@/lang/types';
import ImageBoxFuture from './image-box-future';
import Link from 'next/link';
import { MyOrderModel } from '@/logic/services/my-booking';
import { checkIsRtl } from '@/lang/utils';
import { isWalletFeatureEnabled } from '@/constants/wallet';
import { pxToRem } from '@/theme/typography';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/dist/client/router';

const oldUserBookingCardEmptyIcon =
  '/icons/my-booking/old-user-booking-card-empty-icon.svg';

interface MyBookingCardProps {
  item: MyOrderModel;
  index: number;
  historyType?: boolean;
}
export const MyBookingCard: FC<MyBookingCardProps> = myBookingCardProps => {
  const theme = useTheme();
  const { locale } = useRouter();
  const intl = useIntl();

  const isRtl = checkIsRtl(locale as ILocale);

  const isOrderCanceled = myBookingCardProps?.item?.status === 'CANCELLED';

  return (
    <Grow
      in
      style={{
        transformOrigin: '0 0 0',
      }}
      {...{ timeout: 1000 + myBookingCardProps?.index * 500 }}
    >
      <Stack
        key={myBookingCardProps?.item?.id}
        component='article'
        direction='row'
        spacing={{
          mobile: '8px',
          tablet: pxToRem(25),
        }}
        sx={{
          alignItems: 'center',
          border: `1px solid ${theme.palette.lightGrey}`,
          boxShadow: theme.customShadows.cardShadow,
          borderRadius: '20px',
          background: theme.palette.common.white,
          padding: '4px',
          height: {
            tablet: pxToRem(195),
          },
          maxWidth: '431px',
          marginBottom: {
            mobile: '20px',
            tablet: pxToRem(40),
          },
          marginX: {
            mobile: 'auto',
            tablet: '0px',
          },
        }}
      >
        <ImageBoxFuture
          img={
            myBookingCardProps?.item?.isOldBooking
              ? oldUserBookingCardEmptyIcon
              : myBookingCardProps?.item?.film?.image
          }
          title={
            myBookingCardProps?.item?.film?.imageAlt ||
            intl.formatMessage({
              id: 'alt.booking_card_img',
            })
          }
          layout='intrinsic'
          objectFit='cover'
          width='1600px'
          height='2400px'
          sx={{
            width: {
              mobile: '130px',
              tablet: pxToRem(122),
            },
            boxShadow: 'inset 0px 9px 30px rgba(0, 0, 0, 0.25)',
            borderRadius: '20px',
            overflow: 'hidden',
          }}
        />
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <Stack
            direction={{
              mobile: 'column',
              tablet: 'row',
            }}
            sx={{
              marginBottom: {
                mobile: '8px',
                tablet: pxToRem(32),
              },
              justifyContent: {
                tablet: 'space-between',
              },
              alignItems: {
                mobile: 'flex-start',
                tablet: 'center',
              },
            }}
          >
            <Typography
              component='h4'
              sx={{
                color: theme.palette.grey[90],
                fontWeight: 700,
                fontSize: { tablet: pxToRem(18), mobile: '13px' },
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                [theme.breakpoints.up('tablet')]: {
                  WebkitLineClamp: '1',
                },
                [theme.breakpoints.down('tablet')]: {
                  WebkitLineClamp: '3',
                },
              }}
            >
              {myBookingCardProps?.item?.isOldBooking
                ? myBookingCardProps?.item?.filmTitle
                : myBookingCardProps?.item?.film?.title}
            </Typography>
            {isWalletFeatureEnabled && isOrderCanceled && (
              <CancelTicketChip
                sx={{
                  marginTop: {
                    mobile: '5px',
                    tablet: '0px',
                  },
                }}
              />
            )}
          </Stack>

          <Box
            component='div'
            sx={{
              display: 'flex',
              alignItems: { tablet: 'center', mobile: ' flex-start' },
              justifyContent: 'space-between',
              [theme.breakpoints.down('tablet')]: {
                flexDirection: 'column',
              },
            }}
          >
            <Box
              component='p'
              sx={{
                fontSize: { tablet: pxToRem(16), mobile: '13px' },
                color: theme.palette.grey[50],
                lineHeight: { tablet: '150%', mobile: '19.5px' },
              }}
            >
              {!myBookingCardProps?.item?.isOldBooking
                ? myBookingCardProps?.item?.cinema?.city?.name
                : ''}
              {myBookingCardProps?.item?.cinema?.city?.name &&
              !myBookingCardProps?.item?.isOldBooking
                ? ','
                : ''}
              {!myBookingCardProps?.item?.isOldBooking
                ? myBookingCardProps?.item?.cinema?.name
                : ''}

              {myBookingCardProps?.item?.isOldBooking
                ? myBookingCardProps?.item?.cinemaTitle
                : ''}

              <br />
              {myBookingCardProps?.item?.seats}
              <br />
              <Box component='time'>
                {DateTime.fromISO(
                  myBookingCardProps?.item?.isOldBooking
                    ? myBookingCardProps?.item?.session?.showtime
                    : myBookingCardProps?.item?.showtime,
                  {
                    locale,
                  }
                ).toFormat('h:mm a . LLL d, yy')}
              </Box>
            </Box>
            <Link
              href={{
                pathname: `/user-area/${
                  myBookingCardProps?.historyType ? 'view-history' : 'upcoming-booking'
                }/${myBookingCardProps?.item?.id}`,
              }}
              legacyBehavior
        passHref
            >
              <IconButton
                sx={{
                  width: '42px',
                  height: '42px',
                  background: theme.palette.secondary.main,
                  color: 'white',
                  [theme.breakpoints.down('tablet')]: {
                    mt: '8px',
                  },
                }}
              >
                {isRtl ? (
                  <ArrowBackIosIcon sx={{ fontSize: '18px' }} />
                ) : (
                  <ArrowForwardIosIcon sx={{ fontSize: '18px' }} />
                )}
              </IconButton>
            </Link>
          </Box>
        </Box>
      </Stack>
    </Grow>
  );
};

export default MyBookingCard;
